import * as React from "react";
import type { Omittable } from "../lib/props";

// ---------------------------------------------------------------------------
// useMouseDrag — click-and-drag a scroll container to pan it
// ---------------------------------------------------------------------------
//
// One delegated pointerdown listener on the container. A 5px movement
// threshold (measured on the allowed axes only) separates a pan from a plain
// click, so clicks on rows/cells inside the container still land untouched.
// Once a pan activates, the pointerup's synthesized click is swallowed with a
// one-shot capture-phase listener — a container full of clickable rows must
// not fire a row click at the end of a pan.
//
// Mouse pointers only: touch pans the scroll container natively, and hijacking
// it here would fight the platform gesture.

const DRAG_THRESHOLD_PX = 5;

/**
 * Elements where press-and-DRAG has a meaning of its own — a pan never starts
 * on them. Merely *clickable* elements (buttons, links, rows) are deliberately
 * not here: a real click stays under the movement threshold and lands
 * normally, and a pan past it swallows the synthesized click. Guarding all of
 * `button` would make a table of editable cells (each a full-cell trigger
 * button) refuse to pan almost everywhere.
 */
const OWNS_PRESS_DRAG_SELECTOR =
  "input, textarea, select, [contenteditable='true'], [role='slider'], " +
  "[draggable='true'], [data-dnd-draggable]";

export type UseMouseDragOptions = Omittable<{
  /** The scrollable element to pan. */
  ref: React.RefObject<HTMLElement | null>;
  /** Allow panning on the horizontal axis. Defaults to true. */
  horizontal?: boolean;
  /** Allow panning on the vertical axis. Defaults to true. */
  vertical?: boolean;
  /** When false, no listener is attached. */
  enabled?: boolean;
  /**
   * Extra predicate: return `true` to leave the press alone. For elements
   * that run their own drag gesture from a delegated listener without
   * stopping propagation (e.g. a reorderable header cell) — the built-in
   * interactive-element guard cannot know about those.
   */
  shouldIgnore?: (target: Element) => boolean;
}>;

export function useMouseDrag({
  ref,
  horizontal = true,
  vertical = true,
  enabled = true,
  shouldIgnore,
}: UseMouseDragOptions): void {
  // Latest-ref so an inline `shouldIgnore` lambda doesn't re-attach the
  // listeners every render.
  const shouldIgnoreRef = React.useRef(shouldIgnore);
  shouldIgnoreRef.current = shouldIgnore;

  React.useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    let pending: {
      pointerId: number;
      startX: number;
      startY: number;
      scrollLeft: number;
      scrollTop: number;
      canX: boolean;
      canY: boolean;
    } | null = null;
    let active = false;
    let raf: number | null = null;
    let lastX = 0;
    let lastY = 0;
    let prevCursor = "";
    let prevUserSelect = "";

    // One scroll write per frame, not per pointermove: the containers this
    // pans are virtualized, so every scroll offset change re-windows rows,
    // and writing on each move floods that work between frames.
    const applyScroll = () => {
      raf = null;
      // A frame is only scheduled by an active move, and `cleanupDrag` cancels
      // a pending frame in the same block that clears `pending` — a frame that
      // fires always has a live drag.
      const drag = pending!;
      if (drag.canX) el.scrollLeft = drag.scrollLeft - (lastX - drag.startX);
      if (drag.canY) el.scrollTop = drag.scrollTop - (lastY - drag.startY);
    };

    // Images and links are natively draggable, and the browser's drag ghost
    // would steal the gesture mid-pan. Attached only while a press is armed.
    const suppressNativeDrag = (e: DragEvent) => {
      e.preventDefault();
    };

    const cleanupDrag = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.removeEventListener("dragstart", suppressNativeDrag);
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
      if (active) {
        // The browser releases capture on pointerup by itself; this covers an
        // unmount mid-drag.
        if (pending && el.hasPointerCapture(pending.pointerId)) {
          el.releasePointerCapture(pending.pointerId);
        }
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
      }
      pending = null;
      active = false;
    };

    const onMove = (e: PointerEvent) => {
      // `pending` is set by the pointerdown that attached this listener, and
      // the only thing that clears it is `cleanupDrag`, which detaches the
      // listener in the same statement block. A move here always has a drag.
      const drag = pending!;
      lastX = e.clientX;
      lastY = e.clientY;
      if (!active) {
        // Threshold on the allowed axes only: a vertical pull on a
        // horizontal-only container is a click (or a native text selection),
        // not a pan that happens to move nothing.
        const dx = drag.canX ? e.clientX - drag.startX : 0;
        const dy = drag.canY ? e.clientY - drag.startY : 0;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD_PX) return;
        active = true;
        // Capture the pointer for the rest of the gesture. This does two
        // jobs: rows scrolling under the held pointer stop receiving
        // enter/leave/hover (whose per-row state churn makes the pan stutter),
        // and an overlay trigger under the cursor never sees the pointerup
        // that would open it — click suppression alone can't catch openers
        // wired to pointer events.
        el.setPointerCapture(drag.pointerId);
        prevCursor = document.body.style.cursor;
        prevUserSelect = document.body.style.userSelect;
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
      }
      // Grab semantics: the content follows the hand, so the scroll offset
      // moves opposite the pointer.
      if (raf === null) raf = requestAnimationFrame(applyScroll);
      e.preventDefault();
    };

    const suppressClick = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onUp = () => {
      const wasActive = active;
      cleanupDrag();
      if (!wasActive) return;
      // The click this pointerup synthesizes dispatches in the same task,
      // before any timeout — swallow it at the window so a pan that started
      // on a clickable row doesn't end by activating the row. The timeout
      // detaches the guard when no click follows (pointercancel, or a
      // release outside the window).
      window.addEventListener("click", suppressClick, true);
      setTimeout(() => {
        window.removeEventListener("click", suppressClick, true);
      }, 0);
    };

    const onDown = (e: PointerEvent) => {
      // Touch and pen pan natively; this gesture is the mouse's substitute.
      if (e.pointerType !== "mouse" || e.button !== 0 || pending) return;
      // A dispatched pointer event on an element listener always carries an
      // Element target; the DOM type is wide because an event that was never
      // dispatched has none.
      const target = e.target as Element;
      if (target.closest(OWNS_PRESS_DRAG_SELECTOR)) return;
      if (shouldIgnoreRef.current?.(target)) return;
      // Arm only for axes that can actually move — a container with nothing
      // to scroll must not eat clicks or flash a grabbing cursor.
      const canX = horizontal && el.scrollWidth > el.clientWidth;
      const canY = vertical && el.scrollHeight > el.clientHeight;
      if (!canX && !canY) return;
      // No preventDefault — a plain click must still reach whatever it hit.
      pending = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop,
        canX,
        canY,
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
      document.addEventListener("dragstart", suppressNativeDrag);
    };

    el.addEventListener("pointerdown", onDown);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      cleanupDrag();
    };
  }, [ref, horizontal, vertical, enabled]);
}
